import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { fmtQty } from "./shared";

/**
 * Searchable ingredient picker (Popover + Command). Used by the Waste and
 * Receiving forms so a manager can type "chick" instead of scrolling a
 * 200-row select on a tablet.
 */
export const IngredientPicker = ({ ingredients = [], value, onChange, testId = "ingredient-picker", placeholder = "Choose an ingredient…" }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => ingredients.find((i) => i.id === value) || null, [ingredients, value]);
  // Session 66 — grouped per DEPARTMENT (Kitchen, Bar, Beach Bar …) so a
  // manager picking stock for one department never confuses "Beach Bar
  // bread" with "Kitchen bread". Legacy ingredients fall back to station.
  const groups = useMemo(() => {
    const byKey = new Map();
    [...ingredients]
      .filter((i) => i.active !== false)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      .forEach((i) => {
        const dept = i.department_name || (String(i.category || "").toLowerCase() === "bar" ? "Bar" : "Kitchen");
        if (!byKey.has(dept)) byKey.set(dept, []);
        byKey.get(dept).push(i);
      });
    return [...byKey.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ingredients]);

  const renderItem = (i) => (
    <CommandItem
      key={i.id}
      value={`${i.name} ${i.department_name || i.category || ""}`}
      onSelect={() => { onChange(i.id, i); setOpen(false); }}
      className="flex items-center justify-between gap-2"
      data-testid={`${testId}-option-${i.id}`}
    >
      <span className="flex items-center gap-2">
        <Check className={`h-4 w-4 ${i.id === value ? "opacity-100" : "opacity-0"}`} />
        <span className="truncate">{i.name}</span>
      </span>
      <span className="text-[11px] tabular-nums text-muted-foreground">{fmtQty(i.stock_on_hand)} {i.unit}{Number(i.back_of_house_stock) > 0 ? ` · ${fmtQty(i.back_of_house_stock)} BOH` : ""}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="h-11 w-full justify-between rounded-xl font-normal" data-testid={testId}>
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>{selected ? `${selected.name} (${selected.unit})` : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[280px] rounded-xl p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>No ingredient matches.</CommandEmpty>
            {groups.map(([dept, list]) => <CommandGroup key={dept} heading={dept}>{list.map(renderItem)}</CommandGroup>)}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
